import {
  Participation,
  ParticipationStatus,
  ParticipationUpdateData,
  SelectionPayload,
  StudyType,
  useAcceptParticipationTable,
  useBulkAcceptParticipation,
  useBulkDeclineParticipation,
  useBulkPayParticipation,
  useDeclineParticipationTable,
  useMarkAsParticipated,
  useParticipationStatusUpdate,
  usePayParticipantTable,
  useUndoDeclineParticipation,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { UseMutationResult } from 'react-query';
import { Colors } from '@app/theme';

export enum ActionEnum {
  DECLINE = 'decline',
  DECLINE_UNMODERATED = 'decline_unmoderated',
  REPORT = 'report',
  VALIDATE = 'validate',
  CONFIRM = 'confirm',
  RESTORE = 'restore',
  RESTORE_UNMODERATED = 'restore_unmoderated',
  ACCEPT = 'accept',
  ACCEPT_WITH_QUOTA_UPDATE = 'accept_with_quota_update',
  CANCEL = 'cancel',
  RESCHEDULE = 'reschedule',
  RATE = 'rate',
}

export interface Actions {
  key: ActionEnum;
  status: ParticipationStatus[];
  color: Colors;
  variant: 'muted' | 'plain' | 'transparent';
  text: string;
  singleAction: (participation: Participation) => void;
  bulkAction?: (payload: SelectionPayload) => Promise<void>;
  tooltip?: string;
  supports: StudyType[];
  bulkActionWithFilter?: boolean;
}

export type ActionsConfig = {
  [key in ActionEnum]: Actions;
};

const createCallback = (
  mutation: UseMutationResult<
    Participation,
    Error,
    Participation,
    ParticipationUpdateData | Participation
  >,
) => {
  return (participation: Participation) => mutation.mutateAsync(participation);
};

export const useActions = (
  sessionId: string,
  studyType: StudyType,
  openModal: (
    actionType: ActionEnum,
    data: {
      participation?: Participation;
      sessionId?: string;
      participationIds?: string[];
    },
  ) => void,
  clearSelection: () => void,
) => {
  const { t } = useTranslation();

  const markAsParticipatedOptions = useParticipationStatusUpdate(
    ParticipationStatus.PARTICIPATED,
  );

  const { mutateAsync: bulkDeclineParticipations } =
    useBulkDeclineParticipation(sessionId);
  const { mutateAsync: bulkAcceptParticipations } =
    useBulkAcceptParticipation(sessionId);
  const { mutateAsync: bulkPayParticipations } =
    useBulkPayParticipation(sessionId);

  const acceptMutation = useAcceptParticipationTable();
  const declineMutation = useDeclineParticipationTable();
  const payMutation = usePayParticipantTable(sessionId);
  const restoreMutation = useUndoDeclineParticipation();
  const markAsParticipated = useMarkAsParticipated(markAsParticipatedOptions);

  const accept = useMemo(
    () => createCallback(acceptMutation),
    [acceptMutation],
  );

  const confirmParticipation = useMemo(
    () => createCallback(markAsParticipated),
    [markAsParticipated],
  );

  const decline = useMemo(
    () => createCallback(declineMutation),
    [declineMutation],
  );

  const pay = useMemo(() => createCallback(payMutation as any), [payMutation]);

  const restore = useMemo(
    () => createCallback(restoreMutation),
    [restoreMutation],
  );

  const modalAcceptOptions = useParticipationStatusUpdate(
    ParticipationStatus.ACCEPTED,
  );

  const modalPayOptions = useParticipationStatusUpdate(
    ParticipationStatus.PAYMENT_REQUESTED,
  );

  const modalRescheduleOptions = useParticipationStatusUpdate(
    ParticipationStatus.BOOKED,
  );

  const modalCancelOptions = useParticipationStatusUpdate(
    ParticipationStatus.CANCELED,
  );

  const modalReportOptions = useParticipationStatusUpdate(
    ParticipationStatus.NO_SHOW,
  );

  const actionsConfig = useMemo((): ActionsConfig => {
    return {
      [ActionEnum.ACCEPT]: {
        key: ActionEnum.ACCEPT,
        status: [ParticipationStatus.APPLIED],
        supports: [StudyType.MODERATED, StudyType.UNMODERATED],
        color: 'green',
        variant: 'plain',
        text: t('participants.list.selectionFooter.accept', {
          context: studyType,
        }),
        singleAction: accept,
        bulkAction: async (payload: SelectionPayload) => {
          await bulkAcceptParticipations(payload);
          clearSelection();
        },
        bulkActionWithFilter: true,
      },
      [ActionEnum.ACCEPT_WITH_QUOTA_UPDATE]: {
        key: ActionEnum.ACCEPT_WITH_QUOTA_UPDATE,
        status: [ParticipationStatus.OUT_OF_QUOTA],
        supports: [StudyType.MODERATED],
        color: 'green',
        variant: 'plain',
        text: t('participants.list.selectionFooter.accept', {
          context: studyType,
        }),
        singleAction: (participation) =>
          openModal(ActionEnum.ACCEPT_WITH_QUOTA_UPDATE, { participation }),
      },
      [ActionEnum.CONFIRM]: {
        key: ActionEnum.CONFIRM,
        status: [ParticipationStatus.ACCEPTED, ParticipationStatus.IN_PROGRESS],
        supports: [StudyType.UNMODERATED],
        color: 'green',
        variant: 'plain',
        text: t('participants.list.selectionFooter.confirm'),
        singleAction: confirmParticipation,
      },
      [ActionEnum.RESCHEDULE]: {
        key: ActionEnum.RESCHEDULE,
        status: [ParticipationStatus.BOOKED],
        supports: [StudyType.MODERATED],
        color: 'blue',
        variant: 'plain',
        text: t('participants.list.selectionFooter.reschedule'),
        singleAction: (participation) =>
          openModal(ActionEnum.RESCHEDULE, { participation }),
      },
      [ActionEnum.RATE]: {
        key: ActionEnum.RATE,
        status: [ParticipationStatus.PARTICIPATED],
        supports: [StudyType.MODERATED],
        color: 'blue',
        variant: 'plain',
        text: t('participants.list.selectionFooter.rate'),
        singleAction: (participation) =>
          openModal(ActionEnum.RATE, { participation }),
      },
      [ActionEnum.DECLINE]: {
        key: ActionEnum.DECLINE,
        status: [
          ParticipationStatus.APPLIED,
          ParticipationStatus.ACCEPTED,
          ParticipationStatus.OUT_OF_QUOTA,
        ],
        supports: [StudyType.MODERATED],
        color: 'red',
        variant: 'plain',
        text: t('participants.list.selectionFooter.decline'),
        singleAction: decline,
        bulkAction: async (payload: SelectionPayload) => {
          await bulkDeclineParticipations(payload);
          clearSelection();
        },
        bulkActionWithFilter: true,
      },
      [ActionEnum.DECLINE_UNMODERATED]: {
        key: ActionEnum.DECLINE_UNMODERATED,
        status: [ParticipationStatus.ACCEPTED],
        supports: [StudyType.UNMODERATED],
        color: 'red',
        variant: 'plain',
        text: t('participants.list.selectionFooter.decline'),
        singleAction: decline,
        bulkAction: async (payload: SelectionPayload) => {
          await bulkDeclineParticipations(payload);
          clearSelection();
        },
        bulkActionWithFilter: true,
      },
      [ActionEnum.CANCEL]: {
        key: ActionEnum.CANCEL,
        status: [ParticipationStatus.BOOKED],
        supports: [StudyType.MODERATED],
        color: 'red',
        variant: 'plain',
        text: t('participants.list.selectionFooter.cancel'),
        singleAction: (participation) =>
          openModal(ActionEnum.CANCEL, { participation }),
      },
      [ActionEnum.VALIDATE]: {
        key: ActionEnum.VALIDATE,
        status: [ParticipationStatus.PARTICIPATED],
        supports: [StudyType.UNMODERATED],
        color: 'green',
        variant: 'plain',
        text: t('participants.list.selectionFooter.validate'),
        singleAction: pay,
        bulkAction: async (payload: SelectionPayload) => {
          await bulkPayParticipations(payload);
          clearSelection();
        },
      },
      [ActionEnum.REPORT]: {
        key: ActionEnum.REPORT,
        status: [ParticipationStatus.PARTICIPATED],
        supports: [StudyType.UNMODERATED],
        color: 'red',
        variant: 'muted',
        text: t('participants.list.selectionFooter.report'),
        singleAction: (participation) =>
          openModal(ActionEnum.REPORT, { participation }),
        bulkAction: async (payload: SelectionPayload) => {
          openModal(ActionEnum.REPORT, {
            sessionId: payload.sessionId,
            participationIds: payload.selectedIds || [],
          });
        },
      },
      [ActionEnum.RESTORE]: {
        key: ActionEnum.RESTORE,
        status: [ParticipationStatus.DECLINED],
        supports: [StudyType.MODERATED],
        color: 'blue',
        variant: 'plain',
        text: t('participants.list.selectionFooter.restore', {
          context: studyType,
        }),
        singleAction: restore,
      },
      [ActionEnum.RESTORE_UNMODERATED]: {
        key: ActionEnum.RESTORE_UNMODERATED,
        status: [ParticipationStatus.DECLINED],
        supports: [StudyType.UNMODERATED],
        color: 'blue',
        variant: 'plain',
        text: t('participants.list.selectionFooter.restore', {
          context: studyType,
        }),
        singleAction: accept,
        bulkAction: async (payload: SelectionPayload) => {
          await bulkAcceptParticipations(payload);
          clearSelection();
        },
        bulkActionWithFilter: true,
      },
    };
  }, [accept, decline, pay, restore, t, studyType]);

  return {
    actionsConfig,
    mutationOptions: {
      [ActionEnum.ACCEPT_WITH_QUOTA_UPDATE]: modalAcceptOptions,
      [ActionEnum.VALIDATE]: modalPayOptions,
      [ActionEnum.RESCHEDULE]: modalRescheduleOptions,
      [ActionEnum.CANCEL]: modalCancelOptions,
      [ActionEnum.REPORT]: modalReportOptions,
    },
  };
};

export const getApplicableActionsForSelection = (
  selectedStatuses: ParticipationStatus[],
  actionsConfig: ActionsConfig,
  studyType: StudyType,
): Actions[] => {
  if (selectedStatuses.length === 0) return [];

  return Object.entries(actionsConfig)
    .filter(([, actionConfig]) => {
      if (
        !actionConfig.supports.includes(studyType) ||
        !actionConfig.bulkAction
      ) {
        return false;
      }

      return selectedStatuses.every((s) => actionConfig.status.includes(s));
    })
    .map(([, actions]) => actions as Actions);
};
